import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/Auth/authContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { I18nProvider, useI18n } from './contexts/I18nContext';
import { UiVisibilityProvider } from './contexts/UiVisibilityContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { Sidebar } from './components/navigation/Sidebar';
import { Navbar } from './components/navigation/Navbar';
import { DashboardPage } from './pages/DashboardPage';
import { DevicesPage } from './pages/DevicesPage';
import { ClientsPage } from './pages/ClientsPage';
import { UsersPage } from './pages/UsersPage';
import { SignInPage } from './pages/SignInPage';
import { useClientAccess } from './hooks/useClientAccess';
import { NoClientAccessBanner } from './components/common/NoClientAccessBanner';
import { CookieConsent } from './components/common/CookieConsent';
import { AccessDisabledScreen } from './components/common/AccessDisabledScreen';
import { DeviceTypesPage } from './pages/DeviceTypesPage';
import { OtaUpdatesPage } from './pages/OtaUpdatesPage';
import { ConfigTemplatesPage } from './pages/ConfigTemplatesPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { AdministrationPage } from './pages/AdministrationPage';
import { PortabilityPage } from './pages/PortabilityPage';
import { WhoAmIPage } from './pages/WhoAmIPage';
import { applyThemeColors } from './utils/themeUtils';
import config from './config/env';
import './App.css';
import './styles/auth.css';
import './styles/devices.css';
import './styles/clients.css';
import './styles/modal.css';
import './styles/api-status.css';
import './styles/pages.css';
import './styles/dashboard.css';
import './styles/audit.css';
import './styles/dark-theme.css';
import './styles/enhanced-dark-theme.css';
import { LoadingPage } from './pages/Loading';


const AnepOnlyRoute: React.FC<{allowed: boolean; children: React.ReactElement }> = ({allowed,children,}) => (
      allowed ? children : <Navigate to="/dashboard" replace />);


const MainLayout = () => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const { shouldShowClientUI, canAccessCatalog, canAccessOtaUpdates, canViewAuditLog, isLockedOut, isAnepStaff, isClientAdmin, isSuperAdmin } = useClientAccess(); // Restructiriung the useClientAccess hook to get all access flags in one call

  if (loading) {
      return <LoadingPage />;
  }
  if (!isAuthenticated) {
     return <SignInPage />;
  }
  if (isLockedOut) {
    return <AccessDisabledScreen />;
  }
  return (
    
    <div className="main-layout">
      <Sidebar />
      <div className="main-content">
          <NoClientAccessBanner />
          <ErrorBoundary name={`route:${location.pathname}`} resetKey={location.pathname}>
            <Routes>
              <Route 
              path="/dashboard" 
              element={<DashboardPage />} 
              />
              <Route 
              path="/devices" 
              element={<DevicesPage />} 
              />
              <Route
                path="/clients"
                element={<AnepOnlyRoute allowed={shouldShowClientUI}><ClientsPage /></AnepOnlyRoute>}
              />
              <Route 
                path="/users" 
                element={<UsersPage />} 
              />
              <Route
                path="/device-types"
                element={<AnepOnlyRoute allowed={canAccessCatalog}><DeviceTypesPage /></AnepOnlyRoute>}
              />
              <Route
                path="/ota-updates"
                element={<AnepOnlyRoute allowed={canAccessOtaUpdates}><OtaUpdatesPage /></AnepOnlyRoute>}
              />
              <Route
                path="/config-templates"
                element={<ConfigTemplatesPage />}
              />
              <Route
                path="/audit-log"
                element={<AnepOnlyRoute allowed={canViewAuditLog}><AuditLogPage /></AnepOnlyRoute>}
              />
              <Route
                path="/portability"
                element={<AnepOnlyRoute allowed={isAnepStaff || isClientAdmin}><PortabilityPage /></AnepOnlyRoute>}
              />
              <Route
                path="/administration"
                element={<AnepOnlyRoute allowed={isAnepStaff && isSuperAdmin}><AdministrationPage /></AnepOnlyRoute>}
              />
              <Route path="/settings" element={<Navigate to="/administration" replace />} />
              {config.dev.debug && (
                <Route 
                  path="/whoami" 
                  element={<WhoAmIPage />} 
                />
              )}
              <Route 
                path="/" 
                element={<Navigate to="/dashboard" replace />} 
                />
              <Route 
                path="*" 
                element={<Navigate to="/dashboard" replace />} 
                />
            </Routes>
          </ErrorBoundary>
      </div>
    </div>
  );
};


const AuthCallback = () => {
  const { loading } = useAuth();
  const { t } = useI18n();

  if (!loading) {
    return <Navigate to="/" replace />;
  }
  return (
    <div className="auth-callback">
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('auth.processingAuthentication')}</p>
      </div>
    </div>
  );
};


function App() {
  useEffect(() => {
    applyThemeColors();
  }, []);

  return (
    <I18nProvider>
      <ThemeProvider>
        <AuthProvider>
          <UiVisibilityProvider>
            <LayoutProvider>
              <Router>
                <ErrorBoundary name="app-root">

                  <div className="App">
                    <CookieConsent />
                    <Navbar />
                    <main className="App-main">
                      <Routes>
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/*" element={<MainLayout />} />
                      </Routes>
                    </main>
                  </div>
                  
                </ErrorBoundary>
              </Router>
            </LayoutProvider>
          </UiVisibilityProvider>
        </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}
export default App;
